import { z } from 'zod';

import { StoreProductStatus } from '@nucleus-commerce/api-client/admin';

import { seoOverrideSchema } from '@/features/seo';
import { metadataEntrySchema } from '@/shared/forms/metadata-entry-schema';

const HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

const LOCAL_INPUT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/**
 * Scheduled status changes as `datetime-local` values on the store's wall clock; blank means no
 * schedule. Order is checked here; "must be in the future" is the backend's (the clock moves).
 */
export const scheduleFields = {
  publishAt: z
    .string()
    .trim()
    .refine((v) => v === '' || LOCAL_INPUT.test(v), { error: 'storeCatalog:validation.dateTime' }),
  unpublishAt: z
    .string()
    .trim()
    .refine((v) => v === '' || LOCAL_INPUT.test(v), { error: 'storeCatalog:validation.dateTime' }),
};

export function refineSchedule(
  v: { publishAt: string; unpublishAt: string },
  ctx: z.RefinementCtx,
) {
  // Same-zone wall-clock strings compare in time order.
  if (v.publishAt && v.unpublishAt && v.unpublishAt <= v.publishAt) {
    ctx.addIssue({
      code: 'custom',
      path: ['unpublishAt'],
      message: 'storeCatalog:validation.unpublishAfterPublish',
    });
  }
}

/** Blank string means "inherit the master value"; the mapper turns it into null. */
const override = (max: number) => z.string().trim().max(max);

export const storeProductFormSchema = z
  .object({
    status: z.enum(StoreProductStatus),
    handle: z
      .string()
      .trim()
      .min(1, { error: 'storeCatalog:validation.handleRequired' })
      .max(120)
      .regex(HANDLE, { error: 'storeCatalog:validation.handleFormat' }),
    title: override(255),
    subtitle: override(255),
    descriptionHtml: z.string().trim(),
    tags: z.array(z.string().trim().min(1).max(60)).max(100),
    /** Blank fields inherit the master SEO; null clears the whole override. */
    seo: seoOverrideSchema,
    /** Null inherits the master featured image. */
    featuredMediaId: z.string().nullable(),
    metadata: z.array(metadataEntrySchema),
    position: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d{1,6}$/.test(v), {
        error: 'storeCatalog:validation.positionInteger',
      }),
    ...scheduleFields,
  })
  .superRefine(refineSchedule);

export type StoreProductFormInput = z.input<typeof storeProductFormSchema>;
export type StoreProductFormOutput = z.output<typeof storeProductFormSchema>;
